import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { FloatingList } from './floating-list';
import {
  normalizeText,
  scrollEntryIntoView,
  useOutsidePress,
} from './floating-list.utils';

export interface DsComboboxOption {
  value: string;
  label: string;
  /** Shown right-aligned in mono, e.g. a business partner number; searched as well. */
  hint?: string;
}

export interface DsComboboxGroup {
  /** Heading above the group; leave it out for a list without groups. */
  label?: string;
  options: DsComboboxOption[];
}

interface DsComboboxProps {
  /** Value of the chosen option (`''` for none). With `freeText`, the text in the field. */
  value: string;
  onChange: (value: string) => void;
  groups: DsComboboxGroup[];
  /**
   * Any text is a value, not only the options. While the typed text matches
   * no option, the list ends with an entry that takes it, labelled by
   * `createLabel` (e.g. `„Delta" als neuen Lieferanten verwenden`).
   */
  freeText?: { createLabel: (text: string) => string };
  /** The value is settled: an entry was chosen, Enter was pressed or the field was left. */
  onCommit?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  maxLength?: number;
  mono?: boolean;
  /** Shown when nothing matches the typed text. */
  emptyText?: string;
  'aria-label'?: string;
  className?: string;
  /** The list is at least this wide, even under a narrow field. */
  listMinWidth?: number;
}

/** Entries shown per group; more are left out until the text narrows them down. */
const MAX_PER_GROUP = 100;

type Entry =
  | { kind: 'option'; option: DsComboboxOption }
  | { kind: 'create'; text: string };

interface Section {
  label?: string;
  entries: { entry: Entry; index: number }[];
  hidden: number;
}

/**
 * Field with a list to choose from: a click (or ↓) opens the list, typing
 * filters it, ↑ ↓ Enter choose and Esc closes it. With `freeText` the field
 * also takes a value that is not in the list. The list opens in a layer of its
 * own, so it is never cut off by a dialog.
 */
export function DsCombobox({
  value,
  onChange,
  groups,
  freeText,
  onCommit,
  placeholder,
  disabled = false,
  autoFocus,
  maxLength,
  mono = false,
  emptyText = 'Keine Treffer',
  'aria-label': ariaLabel,
  className = '',
  listMinWidth,
}: DsComboboxProps) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  // Strict mode only: what is typed to search, apart from the value.
  const [query, setQuery] = useState('');
  // The list is filtered only once something was typed after opening it.
  const [typed, setTyped] = useState(false);
  const [highlight, setHighlight] = useState(-1);

  const allOptions = useMemo(() => groups.flatMap((g) => g.options), [groups]);
  const selectedOption = allOptions.find((o) =>
    freeText
      ? normalizeText(o.label) === normalizeText(value)
      : o.value === value,
  );
  const search = typed ? normalizeText(freeText ? value : query) : '';

  const { sections, entries } = useMemo(() => {
    const flat: Entry[] = [];
    const built: Section[] = [];
    for (const group of groups) {
      const matches = search
        ? group.options.filter(
            (o) =>
              normalizeText(o.label).includes(search) ||
              (o.hint != null && normalizeText(o.hint).includes(search)),
          )
        : group.options;
      if (matches.length === 0) continue;
      const shown = matches.slice(0, MAX_PER_GROUP);
      built.push({
        label: group.label,
        entries: shown.map((option) => {
          flat.push({ kind: 'option', option });
          return { entry: flat[flat.length - 1], index: flat.length - 1 };
        }),
        hidden: matches.length - shown.length,
      });
    }
    const text = value.trim();
    if (
      freeText &&
      text !== '' &&
      !allOptions.some((o) => normalizeText(o.label) === normalizeText(text))
    ) {
      flat.push({ kind: 'create', text });
      built.push({
        entries: [{ entry: flat[flat.length - 1], index: flat.length - 1 }],
        hidden: 0,
      });
    }
    return { sections: built, entries: flat };
  }, [groups, allOptions, search, freeText, value]);

  const active = highlight < entries.length ? highlight : -1;

  useEffect(() => {
    if (open && active >= 0) scrollEntryIntoView(listId, active);
  }, [open, active, listId]);

  const close = useCallback(() => {
    setOpen(false);
    setTyped(false);
    setQuery('');
    setHighlight(-1);
  }, []);

  const openList = () => {
    if (disabled || open) return;
    setOpen(true);
    setTyped(false);
    setQuery('');
    const selectedIndex = selectedOption
      ? entries.findIndex(
          (e) => e.kind === 'option' && e.option === selectedOption,
        )
      : -1;
    setHighlight(selectedIndex);
  };

  const choose = (entry: Entry) => {
    const next = entry.kind === 'option' ? entry.option.value : entry.text;
    onChange(next);
    onCommit?.(next);
    close();
  };

  const pressRefs = useMemo(() => [rootRef, listRef], []);
  useOutsidePress(open, pressRefs, close);

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!open) openList();
        else setHighlight(Math.min(active + 1, entries.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (open) setHighlight(Math.max(active - 1, freeText ? -1 : 0));
        break;
      case 'Enter':
        if (open && active >= 0) {
          e.preventDefault();
          choose(entries[active]);
        } else if (freeText) {
          onCommit?.(value);
          close();
        }
        break;
      case 'Escape':
        if (open) {
          // Close the list only, not the dialog around the field.
          e.preventDefault();
          e.stopPropagation();
          close();
        }
        break;
      case 'Tab':
        close();
        break;
    }
  };

  const shown = freeText
    ? value
    : open
      ? query
      : (selectedOption?.label ?? value);
  const noMatch = entries.length === 0;

  return (
    <div
      ref={rootRef}
      className={['ds-combobox', disabled && 'is-disabled', className]
        .filter(Boolean)
        .join(' ')}
    >
      <input
        className={['ds-input', mono && 'is-mono'].filter(Boolean).join(' ')}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={
          open && active >= 0 ? `${listId}-opt-${active}` : undefined
        }
        aria-label={ariaLabel}
        value={shown}
        placeholder={
          !freeText && open
            ? (selectedOption?.label ?? placeholder)
            : placeholder
        }
        disabled={disabled}
        autoFocus={autoFocus}
        maxLength={maxLength}
        autoComplete="off"
        onClick={openList}
        onChange={(e) => {
          if (freeText) onChange(e.target.value);
          else setQuery(e.target.value);
          if (!open) setOpen(true);
          setTyped(true);
          setHighlight(freeText ? -1 : 0);
        }}
        onKeyDown={onKeyDown}
        onBlur={() => {
          if (freeText) onCommit?.(value);
          close();
        }}
      />
      <span className="ds-combobox-chevron" aria-hidden>
        ▾
      </span>
      <FloatingList
        ref={listRef}
        anchorRef={rootRef}
        open={open}
        id={listId}
        role="listbox"
        ariaLabel={ariaLabel}
        minWidth={listMinWidth}
      >
        {sections.map((section, i) => (
          <div
            key={section.label ?? `section-${i}`}
            role="group"
            aria-label={section.label}
          >
            {section.label && (
              <div className="ds-floating-list-heading">{section.label}</div>
            )}
            {section.entries.map(({ entry, index }) => {
              const isCreate = entry.kind === 'create';
              const selected = !isCreate && entry.option === selectedOption;
              return (
                <div
                  key={isCreate ? 'create' : `${entry.option.value}-${index}`}
                  id={`${listId}-opt-${index}`}
                  role="option"
                  aria-selected={selected}
                  className={[
                    'ds-floating-list-option',
                    isCreate && 'is-create',
                    selected && 'is-selected',
                    index === active && 'is-active',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onMouseEnter={() => setHighlight(index)}
                  onClick={() => choose(entry)}
                >
                  {isCreate ? (
                    <span className="ds-floating-list-label">
                      + {freeText?.createLabel(entry.text)}
                    </span>
                  ) : (
                    <>
                      <span className="ds-floating-list-label">
                        {entry.option.label}
                      </span>
                      {entry.option.hint && (
                        <span className="ds-floating-list-hint">
                          {entry.option.hint}
                        </span>
                      )}
                    </>
                  )}
                </div>
              );
            })}
            {section.hidden > 0 && (
              <div className="ds-floating-list-note">
                {section.hidden} weitere – zum Eingrenzen weiter tippen
              </div>
            )}
          </div>
        ))}
        {noMatch && <div className="ds-floating-list-note">{emptyText}</div>}
      </FloatingList>
    </div>
  );
}
